import { HomeIcon, Menu } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import ChatInput from "../components/ChatInput";
import ChatMessage from "../components/ChatMessage";
import ChatSidebar from "../components/ChatSidebar";
import { useChatStorage } from "../hooks/useChatStorage";
import { useStreamResponse } from "../hooks/useStreamResponse";
import { Message } from "../types";
import { formatBillName } from "../utils/utils";

const baseUrl = "https://nigeria-tax-bill-ai-api.onrender.com";

function ChatPage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const bill = queryParams.get("bill");

  // const { selectedBill, setSelectedBill } = useBill();
  const { sessions, createNewSession, updateSession, deleteSession, getSession } = useChatStorage();
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Load messages when currentSessionId changes
  useEffect(() => {
    if (currentSessionId) {
      const session = getSession(currentSessionId);
      if (session) {
        setMessages(session.messages);
      } else {
        setMessages([]);
      }
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]);

  const [chatHistory, setChatHistory] = useState<
    Array<{ content: string; role: string }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleStreamChunk = useCallback((chunk: string) => {
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];
      if (lastMessage?.role === "model") {
        const updatedMessages = [
          ...prev.slice(0, -1),
          {
            ...lastMessage,
            content: lastMessage.content + chunk,
          },
        ];
        
        // Sync with storage periodically or on completion? 
        // For smoother UX, we might want to sync only when streaming is done or throttled.
        // But for simplicity in this step, let's sync local state first. 
        // We'll update storage in the useEffect below that watches messages.
        return updatedMessages;
      }
      return prev;
    });
  }, []);

  const {
    isStreaming,
    error: streamError,
    processStream,
  } = useStreamResponse(handleStreamChunk);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const newHistory = messages.map((msg) => ({
      content: msg.content,
      role: msg.role,
    }));
    setChatHistory(newHistory);
    
    // Update storage if we have a session
    if (currentSessionId && messages.length > 0) {
      const currentSession = getSession(currentSessionId);
      // Only update if valid session and messages have actually changed (reference check)
      // This prevents "viewing" a chat from updating its timestamp and moving it to the top
      if (currentSession && currentSession.messages !== messages) {
        updateSession(currentSessionId, messages);
      }
    }
  }, [messages, currentSessionId, updateSession]);

  const handleSendMessage = async (
    content: string,
    personality: string | null
  ) => {
    if (!bill) return;
    setInputValue(""); // Clear input on send

    setError(null);
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      role: "user",
      timestamp: new Date(),
      status: 'sent'
    };

    let activeSessionId = currentSessionId;
    let currentMessages = messages;

    const assistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      content: "",
      role: "model",
      timestamp: new Date(),
      status: 'sending'
    };

    if (!activeSessionId) {
       // Fix: Create session with BOTH messages to prevent useEffect from overwriting the assistant bubble
       // when it re-syncs with storage.
       activeSessionId = createNewSession(bill || 'General', [userMessage, assistantMessage]);
       setCurrentSessionId(activeSessionId);
       
       currentMessages = [userMessage, assistantMessage];
       setMessages(currentMessages);
    } else {
       currentMessages = [...messages, userMessage, assistantMessage];
       setMessages(currentMessages);
    }

    setIsLoading(true);

    const updatedHistory = [...chatHistory, { content, role: "user" }];

    try {
      const url = new URL(`${baseUrl}/messageAsStreamWithBillOrActSpecified`);
      url.searchParams.append("billOrAct", bill.toString());
      if (personality) {
        url.searchParams.append("personalities", personality);
      }

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(updatedHistory),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      console.log(response);

      await processStream(response);
    } catch (error) {
      console.error("Error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      setError(errorMessage);
      setMessages((prev) => prev.slice(0, -1)); // Remove empty model message
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          content: `Oops! Something went wrong. Please try again later.`,
          role: "model",
          timestamp: new Date(),
          status: 'error'
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setMessages([]);
    setChatHistory([]);
    setError(null);
    navigate("/");
  };

  const handleNewChat = () => {
    setCurrentSessionId(null);
    setMessages([]);
    setIsSidebarOpen(false); // On mobile, close sidebar
    // Optional: Navigate to user with clean state, but URL param ?bill=... must stay
    // The current state reset is enough to look like a new chat until they type
  };

  const handleDeleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteSession(sessionId);
    if (currentSessionId === sessionId) {
      handleNewChat();
    }
  };

  const handleBranch = (messageId: string) => {
    const messageIndex = messages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) return;

    const newHistory = messages.slice(0, messageIndex + 1);
    const newSessionId = createNewSession(bill || "General", newHistory);
    setCurrentSessionId(newSessionId);
  };

  const handleRetry = (message: Message) => {
    // Determine personality if needed, for now null
    const personality = null;
    
    // Remove this message and the potential failed error message following it
    // Actually, usually we just want to re-trigger send with this content.
    // Ideally we remove this specific message from state and re-submit.
    
    // Simple approach: Delete this message from UI and call handleSendMessage
    const newMessages = messages.filter(m => m.id !== message.id && m.status !== 'error'); // also remove error msg if any
    setMessages(newMessages);
    updateSession(currentSessionId!, newMessages); // Sync removal
    
    handleSendMessage(message.content, personality);
  };

  const handleEdit = (messageId: string, newContent: string) => {
    const messageIndex = messages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) return;

    // Remove this message and subsequent messages
    const newMessages = messages.slice(0, messageIndex);
    setMessages(newMessages);
    updateSession(currentSessionId!, newMessages);
    
    // Send the NEW content
    handleSendMessage(newContent, null);
  };

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
      <ChatSidebar
        sessions={sessions}
        currentSessionId={currentSessionId}
        onSelectSession={(id) => {
          setCurrentSessionId(id);
          setIsSidebarOpen(false);
        }}
        onDeleteSession={handleDeleteSession}
        onNewChat={handleNewChat}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col h-full relative w-full"> 
        <header className="bg-white shadow-sm flex-none z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                 <button
                  onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                  className="md:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Menu size={24} />
                </button>

                <button
                  onClick={handleBack}
                  className="text-gray-600 hover:text-gray-800 font-medium px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                >
                  <HomeIcon className="mr-1" />
                  <span className="hidden md:inline">Back to Home</span>
                  <span className="md:hidden">Back</span>
                </button>
              </div>
              <div style={{ minWidth: 100 }} className="flex-1 text-right ml-4">
                <h2 className="text-sm md:text-2xl font-bold text-gray-900 truncate">
                  {formatBillName(bill as string)}
                </h2>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-gray-50" style={{ paddingBottom: '80px' }}>
          <div className="max-w-4xl mx-auto px-4 py-8">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <div className="inline-block p-6 bg-white rounded-xl shadow-lg mb-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    Ask About {formatBillName(bill as string)}
                  </h2>
                  <p className="text-gray-600">
                    Start by typing your question below
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {messages.map((message, index) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  isLatest={index === messages.length - 1}
                  isStreaming={
                    (index === messages.length - 1 && isStreaming) || isLoading
                  }
                  onBranch={handleBranch}
                  onRetry={() => handleRetry(message)}
                  onEdit={handleEdit}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </main>

        <div className="bg-white border-t border-gray-100 flex-none z-20">
          <div className="max-w-4xl mx-auto ">
            <ChatInput
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
              isEmpty={messages.length === 0}
              value={inputValue}
              onInputChange={setInputValue}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
